import { useEffect, useState, type ReactNode } from 'react';
import type { Check, CheckStatus } from '../api';

export function Card({ title, hint, actions, children }: {
  title?: string; hint?: ReactNode; actions?: ReactNode; children: ReactNode;
}) {
  return (
    <div className="card">
      {(title || actions) && (
        <div className="row" style={{ marginBottom: hint ? 4 : 12 }}>
          {title && <h3 style={{ margin: 0 }}>{title}</h3>}
          <div className="spacer" />
          {actions}
        </div>
      )}
      {hint && <p className="hint">{hint}</p>}
      {children}
    </div>
  );
}

export function Banner({ kind, children }: { kind: 'error' | 'warning' | 'success'; children: ReactNode }) {
  return <div className={`banner ${kind}`}>{children}</div>;
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <span className="sub">{hint}</span>}
    </label>
  );
}

export function TextField({ label, value, onChange, hint, placeholder, type = 'text', mono }: {
  label: string; value: string; onChange: (v: string) => void;
  hint?: ReactNode; placeholder?: string; type?: string; mono?: boolean;
}) {
  return (
    <Field label={label} hint={hint}>
      <input
        type={type} value={value} placeholder={placeholder}
        style={mono ? { fontFamily: 'var(--mono)', fontSize: 12 } : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function SelectField({ label, value, onChange, options, hint }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; hint?: ReactNode;
}) {
  return (
    <Field label={label} hint={hint}>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

export function CheckBox({ label, hint, checked, onChange }: {
  label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint && <span className="sub">{hint}</span>}
      </span>
    </label>
  );
}

/** Newline-separated textarea bound to a string[] — used for certs, claims, prefixes. */
export function ListField({ label, value, onChange, hint, placeholder }: {
  label: string; value: string[]; onChange: (v: string[]) => void; hint?: ReactNode; placeholder?: string;
}) {
  const [draft, setDraft] = useState(value.join('\n'));
  const joined = value.join('\n');
  useEffect(() => { setDraft(joined); }, [joined]);
  return (
    <Field label={label} hint={hint}>
      <textarea
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onChange(draft.split('\n').map((s) => s.trim()).filter(Boolean))}
      />
    </Field>
  );
}

export function Copyable({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-dim)', marginBottom: 5 }}>{label}</span>}
      <div className="copyable">
        <code className="mono">{value || <span className="faint">not set</span>}</code>
        <button
          className="tiny" disabled={!value}
          onClick={() => {
            navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1400);
            });
          }}
        >{copied ? 'copied' : 'copy'}</button>
      </div>
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  return <span className={`badge ${status}`}>{status}</span>;
}

const ORDER: Record<CheckStatus, number> = { fail: 0, warn: 1, pass: 2, skip: 3 };

export function CheckList({ checks, sorted = false }: { checks: Check[]; sorted?: boolean }) {
  const list = sorted ? [...checks].sort((a, b) => ORDER[a.status] - ORDER[b.status]) : checks;
  return (
    <div className="checklist">
      {list.map((c, i) => (
        <div className="check-row" key={`${c.id}-${i}`}>
          <div className={`check-status ${c.status}`}>{c.status.toUpperCase()}</div>
          <div>
            <div className="label">{c.label}</div>
            {c.detail && <div className="detail">{c.detail}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Code({ children, language }: { children: string; language?: string }) {
  return (
    <>
      {language && <div className="faint small" style={{ marginBottom: 5 }}>{language}</div>}
      <pre className="code mono">{children}</pre>
    </>
  );
}

export function Json({ value }: { value: unknown }) {
  return <pre className="code mono">{JSON.stringify(value, null, 2)}</pre>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Tabs<T extends string>({ tabs, active, onChange }: {
  tabs: { id: T; label: string; count?: number }[]; active: T; onChange: (t: T) => void;
}) {
  return (
    <div className="tabs">
      {tabs.map((t) => (
        <button key={t.id} className={t.id === active ? 'active' : ''} onClick={() => onChange(t.id)}>
          {t.label}
          {t.count !== undefined && <span className="faint"> {t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}
